// src/services/brandsService.js
import axios from 'axios';
import { NODE_BACKEND_API_URL, SUPPLIERS_DEV_MODE } from '../config';

// Mock data for development mode
const MOCK_BRANDS = [
    { BrandId: 1, BrandName: 'BOSCH' },
    { BrandId: 2, BrandName: 'STANLEY' },
    { BrandId: 3, BrandName: 'DEWALT' },
    { BrandId: 4, BrandName: 'MAKITA' },
    { BrandId: 5, BrandName: 'BLACK+DECKER' },
    { BrandId: 6, BrandName: 'TRAMONTINA' },
    { BrandId: 7, BrandName: 'BAHCO' },
    { BrandId: 8, BrandName: 'IRWIN' },
    { BrandId: 9, BrandName: '3M' },
    { BrandId: 10, BrandName: 'TRUPER' },
    { BrandId: 11, BrandName: 'GLADIATOR' },
    { BrandId: 12, BrandName: 'MILWAUKEE' },
    { BrandId: 13, BrandName: 'KARCHER' },
    { BrandId: 14, BrandName: 'EINHELL' },
    { BrandId: 15, BrandName: 'GAMMA' },
];

const MOCK_CATEGORIES = [
    { CategoryId: 1, CategoryName: 'Herramientas Eléctricas' },
    { CategoryId: 2, CategoryName: 'Herramientas Manuales' },
    { CategoryId: 3, CategoryName: 'Pinturas y Accesorios' },
    { CategoryId: 4, CategoryName: 'Ferretería' },
    { CategoryId: 5, CategoryName: 'Plomería' },
    { CategoryId: 6, CategoryName: 'Electricidad' },
    { CategoryId: 7, CategoryName: 'Construcción' },
    { CategoryId: 8, CategoryName: 'Jardinería' },
    { CategoryId: 9, CategoryName: 'Seguridad Industrial' },
    { CategoryId: 10, CategoryName: 'Adhesivos y Selladores' },
    { CategoryId: 11, CategoryName: 'Iluminación' },
    { CategoryId: 12, CategoryName: 'Cerrajería' },
];

/**
 * Fetch all brands from the database
 * Uses /getallbrands endpoint
 * @returns {Promise} Array of brands with {value: BrandId, label: BrandName}
 */
export const getAllBrands = async () => {
    try {
        // Dev mode: return mock data
        if (SUPPLIERS_DEV_MODE) {
            console.log('[DEV MODE] Using mock brands data');
            return MOCK_BRANDS.map(brand => ({
                value: brand.BrandId,
                label: brand.BrandName
            }));
        }

        // Production mode: fetch from API
        const response = await axios.get(`${NODE_BACKEND_API_URL}/getallbrands`);
        
        console.log('Brands API Response:', response.data);
        
        if (response.data && Array.isArray(response.data)) {
            // Transform to Ant Design Select format
            // Convert BrandId to number to match database integer values
            return response.data.map((brand, index) => {
                const id = brand.BrandId || brand.brandId || brand.id || brand.value || index;
                return {
                    value: typeof id === 'string' ? parseInt(id, 10) : id,
                    label: brand.BrandName || brand.brandName || brand.name || brand.label || brand
                };
            });
        }
        
        return [];
    } catch (error) {
        console.error('Error fetching brands:', error);
        throw error;
    }
};

/**
 * Fetch all categories from the database
 * Uses /getcategorynames endpoint
 * @returns {Promise} Array of categories with {value: CategoryId, label: CategoryName}
 */
export const getAllCategories = async () => {
    try {
        // Dev mode: return mock data
        if (SUPPLIERS_DEV_MODE) {
            console.log('[DEV MODE] Using mock categories data');
            return MOCK_CATEGORIES.map(category => ({
                value: category.CategoryId,
                label: category.CategoryName
            }));
        }

        // Production mode: fetch from API
        const response = await axios.get(`${NODE_BACKEND_API_URL}/getcategorynames`);
        
        console.log('Categories API Response:', response.data);
        
        if (response.data && Array.isArray(response.data)) {
            // Transform to Ant Design Select format
            // Convert CategoryId to number to match database integer values
            return response.data.map((category, index) => {
                const id = category.CategoryId || category.categoryId || category.id || category.label || index;
                return {
                    value: typeof id === 'string' ? parseInt(id, 10) : id,
                    label: category.CategoryName || category.categoryName || category.name || category.label || category
                };
            });
        }
        
        return [];
    } catch (error) {
        console.error('Error fetching categories:', error);
        // Return empty array if endpoint doesn't exist yet
        return [];
    }
};

/**
 * Get brand name by ID
 * @param {number} brandId - The brand ID
 * @param {Array} brands - Array of brands
 * @returns {string} Brand name or 'Unknown'
 */
export const getBrandNameById = (brandId, brands) => {
    const brand = brands.find(b => b.value === brandId);
    return brand ? brand.label : 'Unknown';
};

/**
 * Get category name by ID
 * @param {number} categoryId - The category ID
 * @param {Array} categories - Array of categories
 * @returns {string} Category name or 'Unknown'
 */
export const getCategoryNameById = (categoryId, categories) => {
    const category = categories.find(c => c.value === categoryId);
    return category ? category.label : 'Unknown';
};
